// // best working UX
import React, { useState, useEffect, useRef } from "react";
import { View, StyleSheet, TouchableOpacity, Image } from "react-native";
import { useMutation } from "@apollo/client";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../components/common/general/CustomTextInput/CustomTextInput";
import { getYoutubeVideoId } from "../../utils/youtubeUtils.js";
import YoutubeVideoPlayerAdvanced from "../../components/youtube/YoutubeVideoPlayerAdvanced";
import { gql } from "@apollo/client";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import { PlusCircle } from "../../components/svgIcons";
import { SparkleBlue } from "../../components/svgs/common";

// VideoItem component shows a single added video
const VideoItem = ({ video, objectiveText, onRemove, onUpdateUsage }) => {
  const [title, setTitle] = useState(video.title || "Loading...");
  const [description, setDescription] = useState(video.description || "");
  const [isLoading, setIsLoading] = useState(!video.title);
  // const [usage, setUsage] = useState(video.usage || "both"); Will Use this at a Later Time

  useEffect(() => {
    // Only fetch if we don't already have title
    if (!video.title || video.title === "Video" || video.title === "Loading...") {
      fetchVideoInfo();
    }
  }, [video.videoId]);

  const fetchVideoInfo = async () => {
    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?id=${video.videoId}&part=snippet&key=AIzaSyA8Luh5WbUdXzxY6JvmsNfFXdpLUQaZO_c`
      );
      const data = await response.json();

      if (data.items && data.items.length > 0) {
        const videoInfo = data.items[0].snippet;
        setTitle(videoInfo.title);
        setDescription(videoInfo.description);
      } else {
        setTitle("Custom Video");
      }
    } catch (error) {
      console.error("Error fetching video info:", error);
      setTitle("Custom Video");
    } finally {
      setIsLoading(false);
    }
  };

  {
    /* Will Use This at a Later Time */
  }
  // const handleUsageChange = (newUsage) => {
  //   setUsage(newUsage);
  //   onUpdateUsage(objectiveText, video.videoId, newUsage);
  // };

  return (
    <View style={styles.videoItem}>
      <View style={styles.videoPreview}>
        <YoutubeVideoPlayerAdvanced id={video.videoId} />
      </View>
      <View style={styles.videoInfo}>
        <CustomText
          text={isLoading ? "Loading..." : title}
          useTranslationText={false}
          weight="ultraBold"
          numberOfLines={1}
          size="s"
        />
        {description && (
          <CustomText text={description} useTranslationText={false} numberOfLines={3} style={styles.videoDescription} />
        )}

        {/* Will Use This at a Later Time */}
        {/* <View style={styles.videoUsageContainer}>
          <CustomText text="Video Usage" useTranslationText={false} style={styles.usageLabel} />
          <View style={styles.usageOptions}>
            <TouchableOpacity
              style={[styles.usageOption, usage === "rag" && styles.selectedUsage]}
              onPress={() => handleUsageChange("rag")}
            >
              <CustomText
                text="Context for AI"
                useTranslationText={false}
                style={usage === "rag" ? styles.selectedUsageText : styles.usageText}
                size="xs"
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.usageOption, usage === "display" && styles.selectedUsage]}
              onPress={() => handleUsageChange("display")}
            >
              <CustomText
                text="Show to Students"
                useTranslationText={false}
                style={usage === "display" ? styles.selectedUsageText : styles.usageText}
                size="xs"
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.usageOption, usage === "both" && styles.selectedUsage]}
              onPress={() => handleUsageChange("both")}
            >
              <CustomText
                text="Both"
                useTranslationText={false}
                style={usage === "both" ? styles.selectedUsageText : styles.usageText}
                size="xs"
              />
            </TouchableOpacity>
          </View>
        </View> */}

        <TouchableOpacity style={styles.removeButton} onPress={() => onRemove(objectiveText, video.videoId)}>
          <CustomText
            text="Remove Video"
            weight="ultraBold"
            useTranslationText={false}
            style={styles.removeButtonText}
          />
        </TouchableOpacity>
      </View>
    </View>
  );
};

// VideoSuggestion component shows a video recommendation
const VideoSuggestion = ({ video, onAdd }) => {
  return (
    <View style={styles.videoSuggestion}>
      <View style={styles.suggestionThumbnail}>
        <Image
          source={{ uri: `https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg` }}
          style={styles.thumbnailImage}
          resizeMode="cover"
        />
      </View>
      <View style={styles.suggestionInfo}>
        <CustomText text={video.title || "Video"} useTranslationText={false} weight="bold" numberOfLines={2} />
        <CustomText
          text={video.description || ""}
          useTranslationText={false}
          numberOfLines={3}
          style={styles.suggestionDescription}
        />
      </View>
      <CustomButton
        size="s"
        text="Add Video"
        styleType="primaryTransparentStroke"
        textStyle={{ fontWeight: 700 }}
        onPress={() => onAdd(video)}
        leftIcon={<PlusCircle width={16} height={16} />}
      />
    </View>
  );
};

// GraphQL mutation
const CREATE_VIDEO_POOL = gql`
  mutation CreateVideoPool($title: String!, $description: String!, $objectives: [ObjectiveForVideoInput!]!) {
    createVideoPool(title: $title, description: $description, objectives: $objectives) {
      success
      message
      data {
        videoId
        url
        title
        description
        thumbnailUrl
        objectiveText
        objectiveSequence
      }
      count
    }
  }
`;

interface Objective {
  objective: string;
  sequence: number;
}

interface VideoData {
  videoId: string;
  url: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  objectiveText: string;
  objectiveSequence: number;
  usage?: "rag" | "display" | "both";
  isTeacherAdded?: boolean;
}

interface VideoPoolManagerProps {
  title: string;
  description: string;
  objectives: Objective[];
  onVideoPoolUpdate: (
    videoPool: Array<{
      videoId: string;
      objectiveId: string;
      objectiveSequence?: number;
      usage?: "rag" | "display" | "both";
      isTeacherAdded?: boolean;
    }>
  ) => void;
  initialVideoPool?: Array<{
    videoId: string;
    objectiveId: string;
    objectiveSequence?: number;
    usage?: "rag" | "display" | "both";
    isTeacherAdded?: boolean;
  }>;
}

const VideoPoolManager: React.FC<VideoPoolManagerProps> = ({
  title,
  description,
  objectives,
  onVideoPoolUpdate,
  initialVideoPool = [],
}) => {
  const [videosByObjective, setVideosByObjective] = useState<Record<string, VideoData[]>>({});
  const [videoSuggestionsByObjective, setVideoSuggestionsByObjective] = useState<Record<string, VideoData[]>>({});
  const [urlInputByObjective, setUrlInputByObjective] = useState<Record<string, string>>({});
  const [showInputByObjective, setShowInputByObjective] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [objectiveLoading, setObjectiveLoading] = useState({});
  const [error, setError] = useState("");
  const [noVideosFoundFor, setNoVideosFoundFor] = useState({});
  const hasInitializedRef = useRef(false);

  const [createVideoPool] = useMutation(CREATE_VIDEO_POOL);

  // Initialize state for each objective
  useEffect(() => {
    const initialUrlInput = {};
    const initialShowInput = {};
    objectives.forEach((obj) => {
      initialUrlInput[obj.objective] = "";
      initialShowInput[obj.objective] = true;
    });
    setUrlInputByObjective(initialUrlInput);
    setShowInputByObjective(initialShowInput);
  }, [objectives]);

  // Effect to handle initialization from initialVideoPool
  useEffect(() => {
    if (hasInitializedRef.current) return;

    // Initialize the videosByObjective with empty arrays for each objective
    const initialVideosByObjective: Record<string, VideoData[]> = {};
    objectives.forEach((obj) => {
      initialVideosByObjective[obj.objective] = [];
    });

    // Initialize the videoSuggestionsByObjective with empty arrays
    const initialSuggestions: Record<string, VideoData[]> = {};
    objectives.forEach((obj) => {
      initialSuggestions[obj.objective] = [];
    });
    setVideoSuggestionsByObjective(initialSuggestions);

    // If initialVideoPool is provided, populate the videos by objective
    if (initialVideoPool && initialVideoPool.length > 0) {
      initialVideoPool.forEach((video) => {
        const objective = objectives.find(
          (obj) => obj.sequence === video.objectiveSequence || video.objectiveId === obj.objective
        );

        if (objective && objective.objective) {
          const videoData: VideoData = {
            videoId: video.videoId,
            url: `https://www.youtube.com/watch?v=${video.videoId}`,
            title: "", // Will be fetched by VideoItem component
            description: "", // Will be fetched by VideoItem component
            thumbnailUrl: `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`,
            objectiveText: objective.objective,
            objectiveSequence: objective.sequence,
            usage: video.usage || "both",
            isTeacherAdded: video.isTeacherAdded || false,
          };

          if (!initialVideosByObjective[objective.objective]) {
            initialVideosByObjective[objective.objective] = [];
          }

          initialVideosByObjective[objective.objective].push(videoData);

          // Hide URL input for objectives with videos
          setShowInputByObjective((prev) => ({
            ...prev,
            [objective.objective]: false,
          }));
        }
      });

      setTimeout(() => {
        const videoIds = initialVideoPool.map((v) => v.videoId);
      }, 100);
    }

    setVideosByObjective(initialVideosByObjective);
    hasInitializedRef.current = true;
  }, [objectives, initialVideoPool]);

  // Update parent component whenever videosByObjective changes
  useEffect(() => {
    if (hasInitializedRef.current) {
      updateParentVideoPool();
    }
  }, [videosByObjective]);

  const updateParentVideoPool = () => {
    // Prepare the video data for the parent component
    const videoPool = [];

    Object.entries(videosByObjective).forEach(([objectiveText, videos]) => {
      // Find the objective to get its sequence
      const objective = objectives.find((obj) => obj.objective === objectiveText);
      const sequence = objective ? objective.sequence : 0;

      videos.forEach((video) => {
        videoPool.push({
          videoId: video.videoId,
          objectiveId: objectiveText,
          objectiveSequence: sequence,
          usage: video.usage || "both",
          isTeacherAdded: video.isTeacherAdded === true,
        });
      });
    });

    onVideoPoolUpdate(videoPool);
  };

  const handleHideSuggestions = (objectiveText) => {
    setVideoSuggestionsByObjective((prev) => {
      const updated = { ...prev };
      updated[objectiveText] = [];
      return updated;
    });
  };

  const handleGenerateVideos = async () => {
    if (!title || !description || objectives.length === 0) {
      setError("Title, description, and at least one objective are required");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await createVideoPool({
        variables: {
          title,
          description,
          objectives: objectives.map((obj) => ({
            objective: obj.objective,
            sequence: obj.sequence,
          })),
        },
      });

      if (response.data?.createVideoPool?.success) {
        const videos = response.data.createVideoPool.data;

        const suggestionsByObjective = { ...videoSuggestionsByObjective };

        videos.forEach((video: VideoData) => {
          if (!suggestionsByObjective[video.objectiveText]) {
            suggestionsByObjective[video.objectiveText] = [];
          }

          const videoExists = suggestionsByObjective[video.objectiveText].some(
            (existingVideo) => existingVideo.videoId === video.videoId
          );

          const videoAlreadyAdded = videosByObjective[video.objectiveText]?.some(
            (existingVideo) => existingVideo.videoId === video.videoId
          );

          if (!videoExists && !videoAlreadyAdded) {
            suggestionsByObjective[video.objectiveText].push(video);
          }
        });

        setVideoSuggestionsByObjective(suggestionsByObjective);
      } else {
        setError(response.data?.createVideoPool?.message || "Failed to generate videos");
      }
    } catch (err) {
      console.error("Error generating videos:", err);
      setError("An error occurred while generating videos");
    } finally {
      setIsLoading(false);
    }
  };

  // Function to add a video from URL input
  const handleAddVideoFromUrl = (objectiveText) => {
    const urlInput = urlInputByObjective[objectiveText];
    if (!urlInput) return;

    const videoId = getYoutubeVideoId(urlInput);
    if (!videoId) {
      setError("Invalid YouTube URL");
      return;
    }

    const objective = objectives.find((obj) => obj.objective === objectiveText);
    if (!objective) return;

    const newVideo: VideoData = {
      videoId,
      url: urlInput,
      title: "",
      description: "",
      thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      objectiveText: objectiveText,
      objectiveSequence: objective.sequence,
      usage: "both",
      isTeacherAdded: true,
    };

    // Add to videosByObjective
    setVideosByObjective((prev) => {
      const updated = { ...prev };
      if (!updated[objectiveText]) {
        updated[objectiveText] = [];
      }
      updated[objectiveText] = [...updated[objectiveText], newVideo];
      return updated;
    });

    // Clear input and hide input field
    setUrlInputByObjective((prev) => ({
      ...prev,
      [objectiveText]: "",
    }));

    setShowInputByObjective((prev) => ({
      ...prev,
      [objectiveText]: false,
    }));
  };

  // Function to show URL input for adding another video
  const handleShowAddAnother = (objectiveText) => {
    setShowInputByObjective((prev) => ({
      ...prev,
      [objectiveText]: true,
    }));
  };

  // Function to add a suggested video
  const handleAddSuggestedVideo = (objective, video) => {
    // Add to videosByObjective
    setVideosByObjective((prev) => {
      const updated = { ...prev };
      if (!updated[objective]) {
        updated[objective] = [];
      }
      updated[objective] = [...updated[objective], video];
      return updated;
    });

    // Remove from suggestions
    setVideoSuggestionsByObjective((prev) => {
      const updated = { ...prev };
      if (updated[objective]) {
        updated[objective] = updated[objective].filter((v) => v.videoId !== video.videoId);
      }
      return updated;
    });

    // Hide URL input
    setShowInputByObjective((prev) => ({
      ...prev,
      [objective]: false,
    }));
  };

  const handleRemoveVideo = (objectiveText, videoId) => {
    setVideosByObjective((prev) => {
      const updatedVideos = { ...prev };
      if (updatedVideos[objectiveText]) {
        updatedVideos[objectiveText] = updatedVideos[objectiveText].filter((v) => v.videoId !== videoId);
      }

      // If no videos left, show the URL input again
      if (updatedVideos[objectiveText]?.length === 0) {
        setShowInputByObjective((prev) => ({
          ...prev,
          [objectiveText]: true,
        }));
      }

      return updatedVideos;
    });
  };

  // Function to handle updating video usage
  const handleUpdateVideoUsage = (objectiveText, videoId, usage) => {
    setVideosByObjective((prev) => {
      const updatedVideos = { ...prev };
      if (updatedVideos[objectiveText]) {
        updatedVideos[objectiveText] = updatedVideos[objectiveText].map((video) => {
          if (video.videoId === videoId) {
            return { ...video, usage };
          }
          return video;
        });
      }
      return updatedVideos;
    });
  };

  // Function to handle refreshing suggestions
  const handleRefreshSuggestions = (objectiveText) => {
    // Here you would typically call an API to get new suggestions
    // For now, we'll just reset the current suggestions
    setVideoSuggestionsByObjective((prev) => {
      const updated = { ...prev };
      updated[objectiveText] = [];
      return updated;
    });

    // Then regenerate suggestions for this specific objective
    handleGenerateVideosForObjective(objectiveText);
  };

  // Function to generate videos for a specific objective
  const handleGenerateVideosForObjective = async (objectiveText) => {
    if (!title || !description) {
      setError("Title and description are required");
      return;
    }

    const objective = objectives.find((obj) => obj.objective === objectiveText);
    if (!objective) return;

    // Reset the "no videos found" state for this objective
    setNoVideosFoundFor((prev) => ({
      ...prev,
      [objectiveText]: false,
    }));

    // Set loading state for THIS objective only
    setObjectiveLoading((prev) => ({
      ...prev,
      [objectiveText]: true,
    }));

    setError("");

    try {
      const response = await createVideoPool({
        variables: {
          title,
          description,
          objectives: [
            {
              objective: objective.objective,
              sequence: objective.sequence,
            },
          ],
        },
      });

      if (response.data?.createVideoPool?.success) {
        const videos = response.data.createVideoPool.data;

        // Update suggestions for this objective
        setVideoSuggestionsByObjective((prev) => {
          const updated = { ...prev };

          updated[objectiveText] = videos.filter((video) => {
            // Exclude videos that are already added
            const isAlreadyAdded = videosByObjective[objectiveText]?.some(
              (existingVideo) => existingVideo.videoId === video.videoId
            );
            return !isAlreadyAdded;
          });

          // Set flag if no videos were found for this objective
          if (updated[objectiveText].length === 0) {
            setNoVideosFoundFor((prev) => ({
              ...prev,
              [objectiveText]: true,
            }));
          }

          return updated;
        });
      } else {
        setError(response.data?.createVideoPool?.message || "Failed to generate videos");
      }
    } catch (err) {
      console.error("Error generating videos:", err);
      setError("An error occurred while generating videos");
    } finally {
      // Clear loading state for THIS objective only
      setObjectiveLoading((prev) => ({
        ...prev,
        [objectiveText]: false,
      }));
    }
  };

  return (
    <View style={styles.container}>
      <CustomSpacing type="vertical" size="m" />

      <View>
        {objectives.map((objective) => (
          <View key={objective.objective} style={styles.objectiveSection}>
            <View style={styles.objectiveHeaderContainer}>
              <CustomText text={objective.objective} useTranslationText={false} weight="bold" size="s" />
            </View>

            <CustomSpacing type="vertical" size="s" />

            <View style={{ paddingHorizontal: 20 }}>
              {/* Videos added to this objective */}
              {videosByObjective[objective.objective]?.length > 0 && (
                <View style={styles.videosList}>
                  {videosByObjective[objective.objective].map((video, index) => (
                    <VideoItem
                      key={`${video.videoId}-${index}`}
                      video={video}
                      objectiveText={objective.objective}
                      onRemove={handleRemoveVideo}
                      onUpdateUsage={handleUpdateVideoUsage}
                    />
                  ))}
                </View>
              )}

              {/* URL input for adding videos */}
              {showInputByObjective[objective.objective] ? (
                <View style={styles.urlInputContainer}>
                  <CustomText text="YouTube URL" useTranslationText={false} weight="ultraBold" size="m" />
                  <CustomTextInput
                    placeholder="https://www.youtube.com/"
                    value={urlInputByObjective[objective.objective] || ""}
                    onChangeText={(text) =>
                      setUrlInputByObjective((prev) => ({
                        ...prev,
                        [objective.objective]: text,
                      }))
                    }
                    style={styles.urlInput}
                  />
                  <View style={styles.urlButtonsContainer}>
                    <CustomButton
                      text="Add Video to Objective"
                      onPress={() => handleAddVideoFromUrl(objective.objective)}
                      styleType="primaryTransparentStroke"
                      disabled={!urlInputByObjective[objective.objective]}
                      leftIcon={<PlusCircle width={16} height={16} />}
                      textStyle={{ fontWeight: 700 }}
                      size="s"
                    />
                    <CustomSpacing type="horizontal" size="l" />
                    <CustomButton
                      text="Let Axio Suggest a Video"
                      onPress={() => handleGenerateVideosForObjective(objective.objective)}
                      styleType="primaryTransparentStroke"
                      textStyle={{ fontWeight: 700 }}
                      size="s"
                      leftIcon={<SparkleBlue width={16} height={16} />}
                    />
                  </View>

                  {/* Display loading message or no videos found message - now specific to this objective */}
                  {objectiveLoading[objective.objective] && (
                    <CustomText
                      text="Generating video recommendations..."
                      useTranslationText={false}
                      style={styles.loadingText}
                    />
                  )}
                  {noVideosFoundFor[objective.objective] && !objectiveLoading[objective.objective] && (
                    <CustomText
                      text="No Videos Found for the Objective"
                      useTranslationText={false}
                      style={styles.noVideosText}
                    />
                  )}
                </View>
              ) : (
                <View style={styles.addAnotherContainer}>
                  <CustomButton
                    text="Add Another YouTube Video"
                    onPress={() => handleShowAddAnother(objective.objective)}
                    styleType="lightStroke"
                    size="s"
                  />
                  <CustomButton
                    text="Let Axio Suggest a Video"
                    onPress={() => handleGenerateVideosForObjective(objective.objective)}
                    styleType="lightStroke"
                    size="s"
                  />
                </View>
              )}

              {/* Video suggestions for this objective */}
              {videoSuggestionsByObjective[objective.objective]?.length > 0 && (
                <View style={styles.suggestionsContainer}>
                  {videoSuggestionsByObjective[objective.objective].map((video, index) => (
                    <VideoSuggestion
                      key={`suggestion-${video.videoId}-${index}`}
                      video={video}
                      onAdd={() => handleAddSuggestedVideo(objective.objective, video)}
                    />
                  ))}
                  <View style={styles.suggestionsButtonsContainer}>
                    <CustomButton
                      text="Refresh List"
                      onPress={() => handleRefreshSuggestions(objective.objective)}
                      styleType="lightStroke"
                      size="s"
                      style={styles.suggestionButton}
                    />
                    <CustomButton
                      text="Hide Suggestions"
                      onPress={() => handleHideSuggestions(objective.objective)}
                      styleType="lightStroke"
                      size="s"
                      style={styles.suggestionButton}
                    />
                  </View>
                </View>
              )}
            </View>
          </View>
        ))}
      </View>

      {error && <CustomText text={error} useTranslationText={false} style={styles.errorText} />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  errorText: {
    color: "red",
    marginTop: 8,
  },
  objectiveSection: {
    marginBottom: 20,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  urlInputContainer: {
    marginTop: 12,
    marginBottom: 12,
  },
  urlInput: {
    marginTop: 8,
    marginBottom: 12,
    backgroundColor: "#fff",
  },
  urlButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: 8,
  },
  addAnotherContainer: {
    flexDirection: "row",
    marginTop: 12,
    marginBottom: 12,
    gap: 8,
  },
  videosList: {
    marginTop: 12,
    marginBottom: 12,
  },
  videoItem: {
    flexDirection: "row",
    marginBottom: 16,
    backgroundColor: "#fff",
    borderRadius: 8,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    alignItems: "center",
  },
  videoPreview: {
    width: 220,
    aspectRatio: 16 / 9,
    overflow: "hidden",
    alignItems: "center",
    padding: 8,
    borderRadius: 8,
  },
  videoInfo: {
    flex: 1,
    padding: 8,
    justifyContent: "space-between",
  },
  videoDescription: {
    fontSize: 12,
    color: "#666",
    marginTop: 4,
  },
  removeButton: {
    alignSelf: "flex-end",
    marginTop: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: "#f8f8f8",
    borderRadius: 4,
  },
  removeButtonText: {
    color: "#ff3b30",
    fontSize: 14,
  },
  suggestionsContainer: {
    marginTop: 16,
  },
  videoSuggestion: {
    flexDirection: "row",
    marginBottom: 12,
    padding: 8,
    backgroundColor: "#fff",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  suggestionThumbnail: {
    width: 120,
    height: 68,
    borderRadius: 4,
    overflow: "hidden",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
  },
  suggestionInfo: {
    flex: 1,
    paddingHorizontal: 12,
  },
  suggestionDescription: {
    fontSize: 12,
    color: "#666",
    marginTop: 4,
  },
  loadingText: {
    fontStyle: "italic",
    color: "#666",
    textAlign: "center",
    marginTop: 8,
  },
  noVideosText: {
    fontStyle: "italic",
    color: "#ff3b30",
    marginTop: 8,
    textAlign: "center",
  },
  suggestionsButtonsContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 12,
    gap: 8,
    paddingBottom: 16,
  },
  suggestionButton: {
    minWidth: 120,
  },
  objectiveHeaderContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingVertical: 8,
    paddingLeft: 16,
    backgroundColor: "#EAECF0",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
});

export default VideoPoolManager;
